import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Collapse,
  DatePicker,
  Divider,
  Form,
  Input,
  message,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  Typography,
  Upload,
} from 'antd';
import { GlobalOutlined, SyncOutlined } from '@ant-design/icons';
import axios from 'axios';
import * as Component from 'components';
import CustomInputNumber from 'components/CustomInputNumber';
import { EnhancedCastEditor } from 'components/enhanced-cast';
import { I18nFormFields } from 'components/i18n/I18nFormFields';
import CustomSelector, { CustomSelectorTabInterface } from 'components/selector/Selector';
import { BASE_URL } from 'constants/api';
import { IS_PRODUCTION, LANGUAGE_OPTIONS, THUMBNAIL_UPLOAD_STATE } from 'constants/index';
import { globalSettingService } from 'content/global-settings/services/globalSettingService';
import {
  CountryInterface,
  LanguageInterface,
} from 'content/global-settings/types/globalSettingType';
import { contentAssetService } from 'content/library/service/contentAssetService';
import { StreamManifest, VideoAssetSet } from 'content/library/types/contentVideoType';
import { contentService } from 'content/service';
import { ThumbnailInterface } from 'content/type';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { genreApiService } from 'service/api/genreApiService';
import { peopleApiService } from 'service/api/peopleApiService';
import { tagApiService } from 'service/api/tagApiService';
import { videoService } from 'service/api/videoService';
import { GenreInterface } from 'type/genreType';
import { PeopleInterface } from 'type/peopleType';
import { TagInterface } from 'type/tagType';
import { slugify } from 'utils/slugify';
import {
  createDefaultDistributions,
  getApiServiceName,
  SERVICE_OPTIONS,
} from '../../../../constants/serviceDistribution';
import TimeInput from '../../../../shared/components/TimeInput';
import VideoPlayer from '../../../../shared/components/video-player/VideoPlayer';
import odkAdminApiService from '../../../../shared/service/odxAdminApiService';
import { I18nService } from '../../../../shared/services/i18n/i18nService';
import {
  getPlayableVideoUrl,
  hasPlayableVideoUrl,
  VideoType,
} from '../../../../shared/type/videoType';
import { I18nFieldData } from '../../../../shared/types/i18n/i18nTypes';
import { formatDuration } from '../../../../shared/utils/timeFormat';
import Preview from '../../../video/assets/components/preview';
import { MOVIE_AGE_RATING_OPTIONS } from '../constants';
import { movieService } from '../services/movieService';
import { ElevenDbContentInterface, MovieInterface } from '../types/movieType';
import ClipTrailerComponent from './ClipTrailer';
import './MovieEdit.css';

dayjs.extend(utc);
dayjs.extend(timezone);

const { Text } = Typography;

const ManifestSubtitleTags: React.FC<{ url: string }> = ({ url }) => {
  const [languages, setLanguages] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    let isMounted = true;

    if (!url || !url.includes('.m3u8')) {
      setLoading(false);
      return;
    }

    const fetchManifest = async () => {
      try {
        setLoading(true);
        const response = await fetch(url);
        if (!response.ok) throw new Error('Network response was not ok');
        const text = await response.text();
        const lines = text.split('\n');
        const subtitleLines = lines.filter(
          line => line.startsWith('#EXT-X-MEDIA:') && line.includes('TYPE=SUBTITLES')
        );
        const foundLangs = subtitleLines
          .map(line => {
            const match = line.match(/LANGUAGE="([^"]+)"/);
            return match ? match[1] : null;
          })
          .filter(Boolean) as string[];
        const uniqueLangs = Array.from(new Set(foundLangs));
        if (isMounted) setLanguages(uniqueLangs);
      } catch (error) {
        console.error('Failed to fetch or parse manifest for subtitles:', error);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchManifest();
    return () => { isMounted = false; };
  }, [url]);

  if (loading) return <Spin size="small" style={{ marginLeft: '8px' }} />;
  if (languages.length === 0) return null;

  return (
    <div style={{ marginTop: '8px', textAlign: 'center' }}>
      {languages.map(lang => (
        <Tag key={lang} color="blue" style={{ fontSize: '10px', marginRight: '4px' }}>
          {lang}
        </Tag>
      ))}
    </div>
  );
};


const MovieEditSingle: React.FC = () => {
  const [form] = Form.useForm();
  const { id } = useParams<{ id: string }>();
  const [movie, setMovie] = useState<MovieInterface | null>(null);
  const [countries, setCountries] = useState<CountryInterface[]>([]);
  const [writeLanguages, setWriteLanguages] = useState<LanguageInterface[]>([]);
  const [subtitleLanguages, setSubtitleLanguages] = useState<LanguageInterface[]>([]);
  const [searchedPeopleList, setSearchedPeopleList] = useState<PeopleInterface[]>([]);
  const [genreList, setGenreList] = useState<GenreInterface[]>([]);
  const [tagList, setTagList] = useState<TagInterface[]>([]);
  const [enhancedCast, setEnhancedCast] = useState<
    { cast: number; type: 'director' | 'writer' | 'actor' }[]
  >([]);

  // i18n field state
  const [i18nTitle, setI18nTitle] = useState<I18nFieldData>({});
  const [i18nSynopsis, setI18nSynopsis] = useState<I18nFieldData>({});
  // Movie Ratings from Country of Origin
  const [movieRatings, setMovieRatings] = useState<number | undefined>();

  // Video Asset search
  const [videoAssetKeyword, setVideoAssetKeyword] = useState<string>('');
  const [videoAssets, setVideoAssets] = useState<VideoType[]>([]);
  const [selectedVideoAssets, setSelectedVideoAssets] = useState<number[]>([]);
  const [isLoadingVideoAssets, setIsLoadingVideoAssets] = useState(false);
  const [videoAssetCurrentPage, setVideoAssetCurrentPage] = useState<number>(1);
  const [videoAssetTotal, setVideoAssetTotal] = useState<number>(0);

  // Content Video Assets
  const [existingContentVideoAssets, setExistingContentVideoAssets] = useState<VideoAssetSet[]>([]);
  const [linkedVideoAsset, setLinkedVideoAsset] = useState<VideoType | null>(null);

  // Stream manifest regeneration loading state
  const [regeneratingManifests, setRegeneratingManifests] = useState<Record<string, boolean>>({});

  // Per-section loading completion state (tracking data fetch completion)
  const [videoAssetsLoaded, setVideoAssetsLoaded] = useState(false);

  // Service Auto Distribution
  const [distributions, setDistributions] = useState<any[]>([]);

  // 11DB Metadata states
  const [elevenDbContentId, setElevenDbContentId] = useState<string>('');
  const [elevenDbAwards, setElevenDbAwards] = useState<string>('');
  const [elevenDbAwardsCategory, setElevenDbAwardsCategory] = useState<string>('');
  const [elevenDbRelatedSeries, setElevenDbRelatedSeries] = useState<string>('');
  const [elevenDbRelatedTitles, setElevenDbRelatedTitles] = useState<string>('');
  const [elevenDbLinkedArticles, setElevenDbLinkedArticles] = useState<string>('');
  const [elevenDbUserRatings, setElevenDbUserRatings] = useState<string>('');
  const [elevenDbCriticsRatings, setElevenDbCriticsRatings] = useState<string>('');
  const [elevenDbMovieBoxOffice, setElevenDbMovieBoxOffice] = useState<string>('');

  // Thumbnail/Poster states
  const [newThumbnailList, setNewThumbnailList] = useState<ThumbnailInterface[]>([]);
  const [uploadedThumbnailList, setUploadedThumbnailList] = useState<ThumbnailInterface[]>([]);
  const [_selectedThumbnail, setSelectedThumbnail] = useState<ThumbnailInterface | null>(null);
  const [removedUids, setRemovedUids] = useState<string[]>([]);

  // For tracking changed fields
  const changedFieldsRef = useRef<Set<string>>(new Set());

  const markFieldChanged = (...fields: string[]) => {
    fields.forEach(field => {
      changedFieldsRef.current.add(field);
    });
  };

  const handleValuesChange = (changedValues: any) => {
    Object.keys(changedValues).forEach(key => {
      markFieldChanged(key);
    });
  };

  // CP from ODK, ODC states
  const [cpFromOdkOptions, setCpFromOdkOptions] = useState<any[]>([]);
  const [cpFromOdcOptions, setCpFromOdcOptions] = useState<any[]>([]);
  const [defaultCpFromOdcOptions, setDefaultCpFromOdcOptions] = useState<any[]>([]);
  const [cpFromOdcLoading, setCpFromOdcLoading] = useState(false);
  const [resyncLoading, setResyncLoading] = useState(false);

  // Extract display name and environment from API service_name

  const initializeDefaultDistributions = () => {
    setDistributions(createDefaultDistributions());
  };

  const updateDistribution = (serviceName: string, environment: string, updates: any) => {
    setDistributions(prev =>
      prev.map(dist =>
        dist.service_name === serviceName && dist.environment === environment
          ? { ...dist, ...updates }
          : dist
      )
    );
  };

  // Function to normalize language value (lowercase - matches POSTER_LANGUAGE_OPTIONS)
  const normalizeLanguage = (lang: string | undefined): string | undefined => {
    if (!lang) return undefined;

    // First convert to uppercase to compare with LANGUAGE_OPTIONS
    const upperLang = lang.toUpperCase();

    // Match with LANGUAGE_OPTIONS uppercase values
    const langOption = LANGUAGE_OPTIONS.find(opt => opt.value === upperLang);
    if (langOption) {
      // Convert to lowercase since POSTER_LANGUAGE_OPTIONS uses lowercase
      const posterMapping: Record<string, string> = {
        EN: 'en',
        KO: 'ko',
        'ZH-HANS': 'zh-hans',
        'ZH-HANT': 'zh-hant',
        VI: 'vi',
        JA: 'ja',
      };
      return posterMapping[upperLang] || langOption.value.toLowerCase();
    }

    // Convert to lowercase to check mapping
    const lowerLang = lang.toLowerCase();
    const mapping: Record<string, string> = {
      kr: 'ko',
      jp: 'ja',
      zh: 'zh-hans',
      vi: 'vi',
      en: 'en',
      ko: 'ko',
    };

    if (mapping[lowerLang]) {
      return mapping[lowerLang];
    }

    // Default is 'ko' (lowercase)
    return 'ko';
  };

  // Thumbnail/Poster handlers (SeasonEdit2 style)
  const handleThumbnailRemove = (thumbnail: ThumbnailInterface) => {
    if (thumbnail.id && thumbnail.id > 0) {
      // Remove registered thumbnail
      const currentUploadedList = form.getFieldValue('uploadedThumbnailList') || [];
      const currentNewList = form.getFieldValue('newThumbnailList') || [];

      const updatedUploadedList = currentUploadedList.filter(
        (t: ThumbnailInterface) => t.id !== thumbnail.id
      );
      const updatedNewList = currentNewList.filter(
        (t: ThumbnailInterface) => !(t.id && t.id === thumbnail.id)
      );

      form.setFieldValue('uploadedThumbnailList', updatedUploadedList);
      form.setFieldValue('newThumbnailList', updatedNewList);
      setUploadedThumbnailList(updatedUploadedList);
      setNewThumbnailList(updatedNewList);

      if (_selectedThumbnail && _selectedThumbnail.id === thumbnail.id) {
        setSelectedThumbnail(null);
      }
    } else {
      // Remove newly added thumbnail
      const currentNewList = form.getFieldValue('newThumbnailList') || [];
      const updatedNewList = currentNewList.filter(
        (t: ThumbnailInterface) => t.uid !== thumbnail.uid
      );

      form.setFieldValue('newThumbnailList', updatedNewList);
      setNewThumbnailList(updatedNewList);

      if (thumbnail.uid) {
        setRemovedUids(prev => {
          if (!prev.includes(thumbnail.uid!)) {
            return [...prev, thumbnail.uid!];
          }
          return prev;
        });
      }
    }
  };

  const handleThumbnailUpload = async (targetContentId?: number) => {
    const targetId = targetContentId || (id && id !== 'new' ? Number(id) : 0);
    if (!targetId) {
      Modal.error({ title: 'Error', content: 'Please save movie first', okText: 'Confirm' });
      return;
    }

    const loadingMessage = message.loading('Updating...', 0);
    const thumbnailList: ThumbnailInterface[] = form.getFieldValue('thumbnailList') || [];
    const newThumbnailList: ThumbnailInterface[] = form.getFieldValue('newThumbnailList') || [];
    const uploadedThumbnailList: ThumbnailInterface[] =
      form.getFieldValue('uploadedThumbnailList') || [];

    // Normalize language values
    const normalizedNewThumbnailList = newThumbnailList.map(thumb => ({
      ...thumb,
      language: normalizeLanguage(thumb.language),
    }));
    const normalizedUploadedThumbnailList = uploadedThumbnailList.map(thumb => ({
      ...thumb,
      language: normalizeLanguage(thumb.language),
    }));

    // Check duplicate combinations
    const allList = [...normalizedNewThumbnailList, ...normalizedUploadedThumbnailList];
    const comboCounts = allList.reduce<Record<string, number>>((acc, t) => {
      const key = `${t.language}-${t.orientation}`;
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
    const duplicates = Object.entries(comboCounts)
      .filter(([_, cnt]) => cnt > 1)
      .map(([combo]) => combo);

    if (duplicates.length > 0) {
      loadingMessage();
      Modal.error({ title: 'Error', content: `Duplicate language/aspect ratio combination: ${duplicates.join(', ')}`, okText: 'Confirm' });
      return;
    }

    // Calculate thumbnail list to delete
    const removeThumbnails = thumbnailList.filter(
      (uploadedThumbnail: { id: number }) =>
        !normalizedUploadedThumbnailList.some(
          (uploadThumbnail: { id: number }) => uploadThumbnail.id === uploadedThumbnail.id
        )
    );

    try {
      // Same order as SeasonEdit2: delete -> add
      if (removeThumbnails.length > 0) {
        await contentService.removeThumbnail(targetId, removeThumbnails);
      }
      if (normalizedNewThumbnailList.length > 0) {
        await contentService.uploadThumbnail(targetId, normalizedNewThumbnailList);
      }
      loadingMessage();
      message.success('upload success');
      form.resetFields(['newThumbnailList', 'uploadedThumbnailList', 'thumbnailList']);
    } catch (error) {
      loadingMessage();
      console.error('Failed to upload thumbnails:', error);
    }
  };

  const handleUploadWithLanguageAndOrientation =
    (language: string, orientation: string) => (info: any) => {
      const { fileList } = info;

      const existingNewList = form.getFieldValue('newThumbnailList') || [];
      const existingUploadedList = form.getFieldValue('uploadedThumbnailList') || [];

      // Check existing list uids and removedUids
      const existingUids = new Set([
        ...existingNewList.map((t: ThumbnailInterface) => t.uid).filter(Boolean),
        ...existingUploadedList.map((t: ThumbnailInterface) => t.uid).filter(Boolean),
      ]);

      // Filter only uids not in removedUids and not in existing list
      const filteredFileList = fileList.filter(
        (file: any) => !removedUids.includes(file.uid) && !existingUids.has(file.uid)
      );

      // Remove existing thumbnails with same language/orientation combination
      const existingThumbnail = existingUploadedList.find(
        (thumb: ThumbnailInterface) =>
          normalizeLanguage(thumb.language) === normalizeLanguage(language) &&
          thumb.orientation === orientation
      );

      if (existingThumbnail && filteredFileList.length > 0) {
        const updatedUploadedList = existingUploadedList.filter(
          (thumb: ThumbnailInterface) => thumb.id !== existingThumbnail.id
        );
        form.setFieldValue('uploadedThumbnailList', updatedUploadedList);
        setUploadedThumbnailList(updatedUploadedList);
      }

      // Convert only newly added files
      const uploadThumbnailList = filteredFileList.map((file: any, index: number) => {
        const fileUrl = file.originFileObj ? URL.createObjectURL(file.originFileObj) : '';
        return {
          id: -(existingNewList.length + index + 1),
          uid: file.uid,
          file: fileUrl,
          is_representative: false,
          state: THUMBNAIL_UPLOAD_STATE.UPLOADING,
          originFileObj: file.originFileObj,
          orientation: orientation,
          language: normalizeLanguage(language),
        } as ThumbnailInterface;
      });

      // Combine with existing list
      const combined = [...existingNewList, ...uploadThumbnailList];

      // Sync form fields and state
      form.setFieldValue('newThumbnailList', combined);
      setNewThumbnailList(combined);

      // Remove from removedUids (since re-uploaded)
      if (uploadThumbnailList.length > 0) {
        const newUids = uploadThumbnailList.map((t: ThumbnailInterface) => t.uid).filter(Boolean);
        setRemovedUids(prev => prev.filter(uid => !newUids.includes(uid)));
      }
    };

  // Poster list grouped by language (uppercase - matches LANGUAGE_OPTIONS)
  const POSTER_LANGUAGE_OPTIONS = [
    { value: 'en', label: 'English', flag: '🇺🇸' },
    { value: 'ko', label: 'Korean', flag: '🇰🇷' },
    { value: 'zh-hans', label: 'Simplified Chinese', flag: '🇨🇳' },
    { value: 'vi', label: 'Vietnamese', flag: '🇻🇳' },
  ];

  const allThumbnails = useMemo(
    () => [...newThumbnailList, ...uploadedThumbnailList],
    [newThumbnailList, uploadedThumbnailList]
  );

  const groupedByLanguage = useMemo(() => {
    const grouped: Record<string, ThumbnailInterface[]> = {};
    POSTER_LANGUAGE_OPTIONS.forEach(lang => {
      grouped[lang.value] = allThumbnails.filter(
        thumb => normalizeLanguage(thumb.language) === lang.value
      );
    });
    return grouped;
  }, [allThumbnails]);

  const fetchCpFromOdk = async () => {
    const response = await odkAdminApiService.fetchCpFromOdk();
    setCpFromOdkOptions(response);
  };

  const fetchCpFromOdc = async () => {
    const response = await odkAdminApiService.fetchCpFromOdc();
    setCpFromOdcOptions(response);
    setDefaultCpFromOdcOptions(response);
  };

  // ODC search function (with debounce)
  const debouncedSearchCpFromOdc = useCallback(
    debounce(async (searchTerm: string) => {
      if (!searchTerm.trim()) {
        setCpFromOdcOptions(defaultCpFromOdcOptions);
        return;
      }

      setCpFromOdcLoading(true);
      try {
        const result = await odkAdminApiService.searchCpFromOdc(searchTerm);
        setCpFromOdcOptions(result);
      } catch (error) {
        console.error('ODC CP search failed:', error);
        setCpFromOdcOptions([]);
      } finally {
        setCpFromOdcLoading(false);
      }
    }, 300),
    [defaultCpFromOdcOptions]
  );

  // Restore default list when ODC Select box opens
  const handleCpFromOdcDropdownVisibleChange = (open: boolean) => {
    if (open) {
      setCpFromOdcOptions(defaultCpFromOdcOptions);
    }
  };

  // Handle ODC search term change
  const handleCpFromOdcSearch = (value: string) => {
    if (!value.trim()) {
      setCpFromOdcOptions(defaultCpFromOdcOptions);
    } else {
      debouncedSearchCpFromOdc(value);
    }
  };

  // Handle when search term is cleared via ODC X button
  const handleCpFromOdcClear = () => {
    setCpFromOdcOptions(defaultCpFromOdcOptions);
  };

  const fetchElevenDbContent = async () => {
    const response = await movieService.fetchElevenDbContent(Number(id));
    setElevenDbContentId(response.eleven_db_id || '');
    setElevenDbAwards(response.awards || '');
    setElevenDbAwardsCategory(response.awards_category || '');
    setElevenDbRelatedSeries(response.related_series || '');
    setElevenDbRelatedTitles(response.related_titles || '');
    setElevenDbLinkedArticles(response.linked_articles || '');
    setElevenDbUserRatings(response.user_ratings || '');
    setElevenDbCriticsRatings(response.critics_ratings || '');
    setElevenDbMovieBoxOffice(response.movie_box_office || '');
  };

  useEffect(() => {
    if (id && id !== 'new') {
      fetchMovie();
      fetchElevenDbContent();
    } else {
      initializeDefaultDistributions();
      // New Movie has no data to fetch, mark loading as complete
      setVideoAssetsLoaded(true);
      // Initialize thumbnailList for new movie
      form.setFieldValue('thumbnailList', []);
      form.setFieldValue('newThumbnailList', []);
      form.setFieldValue('uploadedThumbnailList', []);
    }
    fetchCountries();
    fetchWriteLanguages();
    fetchSubtitleLanguages();
    fetchGenres();
    fetchTags();
    fetchCpFromOdk();
    fetchCpFromOdc();
    // Initial Video Asset load
    fetchVideoAssets();
  }, [id]);

  const fetchMovie = async () => {
    try {
      const data = await movieService.fetchById(Number(id));
      setMovie(data);

      // Set Form values
      // Convert UTC date/time from server to PST for display (date part only)
      const formattedData = {
        ...data,
        release_date_start: data.release_date_start
          ? (() => {
              // Parse UTC date/time (supports both ISO and YYYY-MM-DD formats)
              let utcDate;
              if (typeof data.release_date_start === 'string') {
                // Check if ISO format (e.g., 2025-11-10T08:00:00.000Z)
                if (
                  data.release_date_start.includes('T') ||
                  data.release_date_start.includes(' ')
                ) {
                  utcDate = dayjs.utc(data.release_date_start);
                } else {
                  // YYYY-MM-DD format case
                  utcDate = dayjs.utc(data.release_date_start, 'YYYY-MM-DD');
                }
              } else {
                utcDate = dayjs.utc(data.release_date_start);
              }

              if (!utcDate.isValid()) {
                return null;
              }

              // Convert to PST and use date part only
              const pstDate = utcDate.tz('America/Los_Angeles');
              // Use date part only (convert to YYYY-MM-DD format and re-parse)
              return dayjs(pstDate.format('YYYY-MM-DD'), 'YYYY-MM-DD');
            })()
          : null,
        on_air_date: (data as any).on_air_date ? dayjs((data as any).on_air_date) : null,
        status: data.status === 'published' ? 'published' : 'unpublished',
        genres: (data as any).genres || [],
        tags: (data as any).tags || [],
      };
      form.setFieldsValue(formattedData);

      // Initialize i18n fields
      if (data.i18n_title) setI18nTitle(data.i18n_title);
      if (data.i18n_synopsis) setI18nSynopsis(data.i18n_synopsis);
      // Initialize Enhanced Cast
      if (data.enhanced_cast) {
        setEnhancedCast(
          data.enhanced_cast.map(item => ({
            cast: item.cast,
            type: item.type,
          }))
        );
        // Fetch people info for cast IDs in Enhanced Cast
        const castIds = data.enhanced_cast.map(item => item.cast);
        if (castIds.length > 0) {
          try {
            const peopleResult = await peopleApiService.fetchByIds(castIds);
            if (peopleResult.results && peopleResult.results.length > 0) {
              setSearchedPeopleList(prev => {
                const existingIds = new Set(prev.map(p => p.id));
                const newPeople = peopleResult.results.filter(p => !existingIds.has(p.id));
                return [...prev, ...newPeople];
              });
            }
          } catch (error) {
            console.error('Failed to fetch people for enhanced cast:', error);
          }
        }
      }

      // Set Movie Ratings
      if (data.score) {
        setMovieRatings(data.score);
      }

      // Set Service Auto Distribution
      if (data.service_auto_distributions && data.service_auto_distributions.length > 0) {
        // Create only items matching current environment
        const currentEnv = IS_PRODUCTION ? 'production' : 'staging';
        const apiDistributions = data.service_auto_distributions;
        const formattedDistributions: any[] = [];

        SERVICE_OPTIONS.forEach(service => {
          // Find by API service_name
          const apiServiceName = getApiServiceName(service.name);
          const apiDist = apiDistributions.find((d: any) => d.service_name === apiServiceName);

          if (apiDist) {
            formattedDistributions.push({
              id: apiDist.id,
              service_name: service.name,
              environment: currentEnv,
              enabled: true,
              auto_publish: apiDist.auto_publish,
              // Use subtitle_langs if directly in API response, otherwise extract from subtitles array
              subtitle_langs:
                apiDist.subtitle_langs ||
                (apiDist.subtitles ? apiDist.subtitles.map((s: any) => s.subtitle_lang) : []),
            });
          } else {
            formattedDistributions.push({
              service_name: service.name,
              environment: currentEnv,
              enabled: false,
              auto_publish: false,
              subtitle_langs: [],
            });
          }
        });

        setDistributions(formattedDistributions);
      } else {
        initializeDefaultDistributions();
      }

      // Set Thumbnails - fetch separately
      try {
        const thumbnailList = await contentService.fetchThumbnailList(Number(id));
        if (thumbnailList && thumbnailList.length > 0) {
          const uploadedThumbnailList: ThumbnailInterface[] = thumbnailList.map(
            (thumbnail: ThumbnailInterface) => {
              return {
                ...thumbnail,
                state: THUMBNAIL_UPLOAD_STATE.UPLOADED,
                originFileObj: undefined,
                language: normalizeLanguage(thumbnail.language),
              };
            }
          );

          const selectedThumbnail = uploadedThumbnailList.find(
            (thumbnail: ThumbnailInterface) => thumbnail.is_representative
          );

          form.setFieldValue('newThumbnailList', []);
          form.setFieldValue('uploadedThumbnailList', uploadedThumbnailList);
          form.setFieldValue('thumbnailList', uploadedThumbnailList);
          setNewThumbnailList([]);
          setUploadedThumbnailList(uploadedThumbnailList);
          setSelectedThumbnail(selectedThumbnail || null);
        } else {
          // Initialize when no thumbnails
          form.setFieldValue('newThumbnailList', []);
          form.setFieldValue('uploadedThumbnailList', []);
          form.setFieldValue('thumbnailList', []);
          setNewThumbnailList([]);
          setUploadedThumbnailList([]);
          setSelectedThumbnail(null);
        }
      } catch (error) {
        console.error('Failed to fetch thumbnails:', error);
        // Continue initialization even on error
        form.setFieldValue('newThumbnailList', []);
        form.setFieldValue('uploadedThumbnailList', []);
        form.setFieldValue('thumbnailList', []);
        setNewThumbnailList([]);
        setUploadedThumbnailList([]);
        setSelectedThumbnail(null);
      }

      // Fetch Content Video Assets
      try {
        const contentVideoAssets = await contentAssetService.fetchContentVideoAssets(Number(id));
        setExistingContentVideoAssets(contentVideoAssets);
        // Set first asset video as linkedVideoAsset
        if (contentVideoAssets.length > 0 && contentVideoAssets[0].video) {
          try {
            // videoService inherits ApiService so fetchById is available
            const videoAsset = await (videoService as any).fetchById(contentVideoAssets[0].video);
            setLinkedVideoAsset(videoAsset);
          } catch (error) {
            console.error('Failed to fetch linked video asset:', error);
          }
        }
      } catch (error) {
        console.error('Failed to fetch content video assets:', error);
      } finally {
        setVideoAssetsLoaded(true);
      }

      // Fetch Genres and Tags
      if (data.genres && data.genres.length > 0) {
        try {
          const genreResponse = await genreApiService.fetchByIds(data.genres);
          setGenreList(genreResponse.results);
        } catch (error) {
          console.error('Failed to fetch genres:', error);
        }
      }

      const tagIds = (data as any).tags || [];
      if (tagIds.length > 0) {
        try {
          // tags can be an ID array or Tag object array
          const tagIdArray = tagIds.map((t: any) => (typeof t === 'number' ? t : t.id));
          const tagResponse = await tagApiService.fetchByIds(tagIdArray);
          setTagList(tagResponse.results);
        } catch (error) {
          console.error('Failed to fetch tags:', error);
        }
      }
    } catch (error) {
      console.error('Failed to fetch movie:', error);
      // Mark loading complete even on main fetch failure
      setVideoAssetsLoaded(true);
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await globalSettingService.fetchCountries(
        new URLSearchParams({ page_size: '1000' })
      );
      setCountries(response.results);
    } catch (error) {
      console.error('Failed to fetch countries:', error);
    }
  };

  const fetchWriteLanguages = async () => {
    try {
      const response = await globalSettingService.fetchLanguages(
        new URLSearchParams({
          type: 'write',
          page_size: '100',
          order_by: 'order',
          order_direction: 'asc',
        })
      );
      setWriteLanguages(response.results);
    } catch (error) {
      console.error('Failed to fetch write languages:', error);
    }
  };

  const fetchSubtitleLanguages = async () => {
    try {
      const response = await globalSettingService.fetchLanguages(
        new URLSearchParams({
          type: 'subtitle',
          page_size: '100',
          order_by: 'order',
          order_direction: 'asc',
        })
      );
      setSubtitleLanguages(response.results);
    } catch (error) {
      console.error('Failed to fetch subtitle languages:', error);
    }
  };

  const fetchGenres = async () => {
    try {
      const response = await genreApiService.fetch(
        new URLSearchParams({ page: '1', page_size: '100' })
      );
      setGenreList(response.results);
    } catch (error) {
      console.error('Failed to fetch genres:', error);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await tagApiService.fetch(
        new URLSearchParams({ page: '1', page_size: '100' })
      );
      setTagList(response.results);
    } catch (error) {
      console.error('Failed to fetch tags:', error);
    }
  };

  const searchPeopleForEnhancedCast = async (value: string): Promise<void> => {
    if (!value.trim()) return;

    const response = await peopleApiService.fetch(
      new URLSearchParams({
        page: '1',
        page_size: '20',
        search: value,
      })
    );
    setSearchedPeopleList(response.results);
  };

  const fetchVideoAssets = async (searchKeyword?: string, page: number = 1) => {
    try {
      setIsLoadingVideoAssets(true);
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: '10',
      });
      if (searchKeyword && searchKeyword.trim()) {
        params.set('search', searchKeyword.trim());
      }
      const response = await videoService.fetchVideos(params);
      setVideoAssets(response.data || []);
      setVideoAssetTotal(response.count || 0);
      setVideoAssetCurrentPage(page);
    } catch (error) {
      console.error('Failed to search video assets:', error);
    } finally {
      setIsLoadingVideoAssets(false);
    }
  };

  const handleVideoAssetSearch = async () => {
    setVideoAssetCurrentPage(1);
    await fetchVideoAssets(videoAssetKeyword, 1);
  };

  const handleVideoAssetPageChange = (page: number) => {
    fetchVideoAssets(videoAssetKeyword, page);
  };

  const handleLinkVideoAssets = async () => {
    if (selectedVideoAssets.length === 0) {
      message.warning('Please select video assets to link');
      return;
    }

    // Set first selected video asset as linkedVideoAsset
    const selectedAsset = videoAssets.find(asset => selectedVideoAssets.includes(asset.id));
    if (selectedAsset) {
      setLinkedVideoAsset(selectedAsset);
      // When existing assets exist and changing to different video, keep existingContentVideoAssets
      // (handled by update/delete logic on save)
      message.success('Video asset linked. Please save to persist the changes.');
    }
  };

  const handleUnlinkVideoAssets = () => {
    if (!linkedVideoAsset) {
      message.warning('No video asset to unlink');
      return;
    }
    setLinkedVideoAsset(null);
    setSelectedVideoAssets([]);
    // Keep existingContentVideoAssets so they can be deleted on save
    message.success('Video asset unlinked successfully');
  };

  // Slug auto-generation from title en
  const handleTitleChange = (value: I18nFieldData) => {
    setI18nTitle(value);
    // Auto-generate slug from English title
    if (value.en) {
      const autoSlug = slugify(value.en);
      form.setFieldValue('slug', autoSlug);
    }
  };

  // Movie Ratings from Country of Origin
  const handleCountryOfOriginChange = async (countryId: number) => {
    form.setFieldValue('country_of_origin', countryId);
  };

  const executeSave = async () => {
    try {
      await form.validateFields();
      const formData = form.getFieldsValue();

      // Prepare i18n data in correct structure (same approach as library Detail.tsx)
      const i18nStructuredData = {
        ...formData,
        i18n: {
          title: i18nTitle,
          synopsis: i18nSynopsis,
        },
      };

      // Sync i18n data with legacy data (using I18nService)
      const finalFormData = I18nService.processMixedFormatData(i18nStructuredData);

      // Create API request data (including i18n fields)
      const apiRequest = I18nService.createApiRequest(finalFormData);

      // Get latest values for genres and tags by directly using form.getFieldValue
      const genresValue = form.getFieldValue('genres') || [];
      const tagsValue = form.getFieldValue('tags') || [];

      // Final movie data (same approach as library Detail.tsx: formValues + apiRequest)
      const movieData: Partial<MovieInterface> & {
        content_type?: string;
        title?: string;
        title_en?: string;
        synopsis?: string;
        synopsis_en?: string;
        on_air_date?: string | null;
      } = {
        ...formData,
        ...apiRequest,
        content_type: 'movie', // Explicitly set content_type
        enhanced_cast: enhancedCast,
        release_date_start: formData.release_date_start
          ? (() => {
              // Interpret user-selected date as PST 00:00:00
              const selectedDate =
                typeof formData.release_date_start === 'string'
                  ? dayjs(formData.release_date_start, 'YYYY-MM-DD')
                  : dayjs(formData.release_date_start);

              if (!selectedDate.isValid()) {
                return null;
              }

              // Set to PST 00:00:00 then convert to UTC
              const pstDate = selectedDate.tz('America/Los_Angeles', true).startOf('day');
              const utcDate = pstDate.utc();

              // Return UTC date/time in ISO format (e.g., 2025-11-10T08:00:00.000Z)
              return utcDate.toISOString();
            })()
          : null,
        // Date fields (convert dayjs objects to strings)
        on_air_date: formData.on_air_date
          ? dayjs.isDayjs(formData.on_air_date)
            ? formData.on_air_date.format('YYYY-MM-DD')
            : typeof formData.on_air_date === 'string'
              ? formData.on_air_date
              : dayjs(formData.on_air_date).format('YYYY-MM-DD')
          : null,
        movie_ratings: movieRatings,
        // CP from ODK, ODC, country_of_origin - explicitly set to null when undefined (so server updates)
        cp_from_odk: formData.cp_from_odk !== undefined ? formData.cp_from_odk : null,
        cp_from_odc: formData.cp_from_odc !== undefined ? formData.cp_from_odc : null,
        country_of_origin:
          formData.country_of_origin !== undefined ? formData.country_of_origin : null,
        service_auto_distributions: (() => {
          // Filter only distributions matching current environment
          const currentEnvironment = IS_PRODUCTION ? 'production' : 'staging';

          // Merge items with same service_name (prevent duplicates)
          const distributionMap = new Map<
            string,
            {
              service_name: string;
              auto_publish: boolean;
              subtitle_langs: string[];
              id?: number;
            }
          >();

          distributions
            .filter(dist => dist.enabled && dist.environment === currentEnvironment)
            .forEach(dist => {
              const apiServiceName = getApiServiceName(dist.service_name);
              const existing = distributionMap.get(apiServiceName);

              if (!existing || (!dist.id && existing.id)) {
                distributionMap.set(apiServiceName, {
                  service_name: apiServiceName,
                  auto_publish: dist.auto_publish,
                  subtitle_langs: dist.subtitle_langs || [],
                });
              }
            });

          return Array.from(distributionMap.values()).map(({ id, ...rest }) => rest);
        })(),
        status:
          formData.status === true || formData.status === 'published' ? 'published' : 'unpublished',
        // Explicitly include genres and tags
        genres: genresValue || [],
        tags: tagsValue || [],
      };

      const elevenDbContent: Partial<ElevenDbContentInterface> = {
        content: Number(id),
        eleven_db_id: elevenDbContentId,
        awards: elevenDbAwards,
        awards_category: elevenDbAwardsCategory,
        related_series: elevenDbRelatedSeries,
        related_titles: elevenDbRelatedTitles,
        linked_articles: elevenDbLinkedArticles,
        user_ratings: elevenDbUserRatings,
        critics_ratings: elevenDbCriticsRatings,
        movie_box_office: elevenDbMovieBoxOffice,
      };

      let movieId: number;
      if (id && id !== 'new') {
        await movieService.update(Number(id), form, movieData);
        await movieService.updateElevenDbContent(Number(id), elevenDbContent);
        movieId = Number(id);
        message.success('Movie updated successfully');

        // Handle Thumbnail upload (SeasonEdit2 style)
        const thumbnailList = form.getFieldValue('thumbnailList') || [];
        const uploadedThumbnailList = form.getFieldValue('uploadedThumbnailList') || [];
        const hasThumbnailChanges =
          newThumbnailList.length > 0 ||
          removedUids.length > 0 ||
          thumbnailList.length !== uploadedThumbnailList.length ||
          thumbnailList.some(
            (t: ThumbnailInterface) =>
              !uploadedThumbnailList.some((ut: ThumbnailInterface) => ut.id === t.id)
          );

        if (hasThumbnailChanges) {
          // Check if originFileObj exists and merge
          const formNewThumbnailList = form.getFieldValue('newThumbnailList') || [];
          const mergedNewThumbnailList = formNewThumbnailList.map(
            (formThumbnail: ThumbnailInterface) => {
              const stateThumbnail = newThumbnailList.find(t => t.uid === formThumbnail.uid);
              return {
                ...formThumbnail,
                originFileObj: stateThumbnail?.originFileObj || formThumbnail.originFileObj,
              };
            }
          );

          // Sync fields
          form.setFieldValue('newThumbnailList', mergedNewThumbnailList);
          form.setFieldValue('uploadedThumbnailList', uploadedThumbnailList);
          // Keep thumbnailList as original data (to find deleted items)

          try {
            await handleThumbnailUpload();
            message.success('Posters updated successfully');
          } catch (error) {
            console.error('Failed to upload posters:', error);
            message.warning('Movie updated but failed to upload some posters');
          }
        }

        // Save Content Video Assets - API call only when changes exist (only when video assets loading complete)
        if (videoAssetsLoaded && linkedVideoAsset?.id) {
          try {
            const linkedVideoId = linkedVideoAsset.id;

            // 1. Assets to delete: existed before but have different video
            const assetsToDelete = existingContentVideoAssets.filter(
              a => a.id && a.video && a.video !== linkedVideoId
            );

            // 2. Find existing asset with same video_id and service_name('')
            const existingAsset = existingContentVideoAssets.find(
              a => a.video === linkedVideoId && a.service_name === ''
            );

            // Check if existing asset with primary=true exists
            const hasPrimaryAsset = existingContentVideoAssets.some(a => a.primary);

            // 3. Check if data changed for existing asset (including primary)
            const needsUpdate =
              existingAsset &&
              existingAsset.id &&
              ((existingAsset.subtitle_ids ?? null) !== null ||
                (existingAsset.is_subtitles_embedded ?? false) !== false ||
                (existingAsset.is_default ?? true) !== true ||
                (existingAsset.after_add_subtitle ?? false) !== false ||
                (!hasPrimaryAsset));

            // 4. Check if new creation is needed
            const needsCreate = !existingAsset;

            const hasChanges = assetsToDelete.length > 0 || needsUpdate || needsCreate;

            if (hasChanges) {
              // Delete assets to remove
              if (assetsToDelete.length > 0) {
                await Promise.all(
                  assetsToDelete.map(a => {
                    if (a.id) {
                      return contentAssetService.deleteContentVideoAsset(a.id);
                    }
                  })
                );
              }

              if (needsUpdate && existingAsset && existingAsset.id) {
                // Update only existing assets with changed data
                await contentAssetService.updateContentVideoAsset(existingAsset.id, {
                  video: linkedVideoAsset.id,
                  subtitle_ids: null,
                  is_subtitles_embedded: false,
                  is_default: true,
                  primary: !hasPrimaryAsset ? true : existingAsset.primary,
                  service_name: '',
                  stream_manifests: null,
                  after_add_subtitle: false,
                });
              } else if (needsCreate) {
                // Create new - auto set primary to true if none exists
                const videoAssetSet: VideoAssetSet = {
                  content: movieId,
                  video: linkedVideoAsset.id,
                  subtitle_ids: null,
                  is_subtitles_embedded: false,
                  is_default: true,
                  primary: !hasPrimaryAsset,
                  service_name: '',
                  stream_manifests: null,
                  after_add_subtitle: false,
                };
                await contentAssetService.saveContentVideoAssets(movieId, [videoAssetSet]);
              }

              // Re-fetch only when changes occurred
              const updatedContentAssets =
                await contentAssetService.fetchContentVideoAssets(movieId);
              setExistingContentVideoAssets(updatedContentAssets);

              message.success('Content video assets saved successfully');
            }
            // Skip without API call if no changes
          } catch (error) {
            console.error('Failed to save content video assets:', error);
            message.warning('Movie updated but failed to save content video assets');
          }
        } else if (videoAssetsLoaded && linkedVideoAsset === null && existingContentVideoAssets.length > 0) {
          // Delete only when no linked assets and existing assets exist
          try {
            await Promise.all(
              existingContentVideoAssets.map(asset => {
                if (asset.id) {
                  return contentAssetService.deleteContentVideoAsset(asset.id);
                }
              })
            );
            setExistingContentVideoAssets([]);
            message.success('Content video assets removed successfully');
          } catch (error) {
            console.error('Failed to delete content video assets:', error);
            message.warning('Movie updated but failed to remove content video assets');
          }
        }

        window.location.reload();
      } else {
        const created = await movieService.create(form, movieData);
        movieId = created.id;
        await movieService.createElevenDbContent(movieId, {
          ...elevenDbContent,
          content: Number(created.id),
        } as ElevenDbContentInterface);

        message.success('Movie created successfully');

        // Handle Thumbnail upload - save posters even on new creation
        if (newThumbnailList.length > 0) {
          try {
            await handleThumbnailUpload(movieId);
            message.success('Posters uploaded successfully');
          } catch (error) {
            console.error('Failed to upload posters:', error);
            message.warning('Movie created but failed to upload some posters');
          }
        }

        // Save Content Video Assets - save even on new creation (same as EpisodeEdit.tsx)
        if (linkedVideoAsset?.id) {
          try {
            const videoAssetSet: VideoAssetSet = {
              content: movieId,
              video: linkedVideoAsset.id,
              subtitle_ids: null,
              is_subtitles_embedded: false,
              is_default: true,
              primary: true, // First video is always primary on new creation
              service_name: '',
              stream_manifests: null,
              after_add_subtitle: false,
            };
            await contentAssetService.saveContentVideoAssets(movieId, [videoAssetSet]);
            message.success('Content video assets created successfully');
          } catch (error) {
            console.error('Failed to save content video assets:', error);
            message.warning('Movie created but failed to save content video assets');
          }
        }

        // Navigate after poster and video assets save completes
        window.location.href = `/movie/edit/${movieId}`;
      }
    } catch (error: any) {
      console.error('Failed to save movie:', error);
    }
  };

  const handleResyncDistribution = () => {
    if (!id || id === 'new') return;
    Modal.confirm({
      title: 'Resync Distribution',
      content: 'Do you want to resync the distribution?',
      okText: 'Resync',
      cancelText: 'Cancel',
      onOk: async () => {
        setResyncLoading(true);
        try {
          const res = await axios.post(
            `${BASE_URL}/api/v1/content/${id}/resync-distribution/`
          );
          message.success(res.data.message || 'Resync completed');
        } catch (err: any) {
          console.error('Resync failed:', err);
        } finally {
          setResyncLoading(false);
        }
      },
    });
  };

  const handleSave = async () => {
    if (!videoAssetsLoaded) {
      Modal.confirm({
        title: 'Data Still Loading',
        content:
          'Some data is still loading. Only the loaded sections will be saved. ' +
          'Unloaded data (e.g., video assets) will not be modified. Continue?',
        okText: 'Save Loaded Data',
        cancelText: 'Wait',
        onOk: () => executeSave(),
      });
      return;
    }
    await executeSave();
  };

  const tabs = useMemo<CustomSelectorTabInterface<any>[]>(() => {
    const genreTab: CustomSelectorTabInterface<GenreInterface> = {
      label: 'Genre',
      selected: genreList.filter(g => {
        const genreIds = form.getFieldValue('genres') || movie?.genres || [];
        return genreIds.includes(g.id);
      }),
      setSelected: (selected: GenreInterface[]) => {
        const genreIds = selected.map(g => g.id);
        form.setFieldValue('genres', genreIds);
        setGenreList(selected);
        markFieldChanged('genres');
      },
      fetchSearch: (search: string) =>
        genreApiService
          .fetch(new URLSearchParams({ search, page: '1', page_size: '100' }))
          .then(res => res.results),
      getLabel: (genre: GenreInterface) => genre.slug || genre.i18n_name?.ko || '',
    };

    const tagTab: CustomSelectorTabInterface<TagInterface> = {
      label: 'Tag',
      selected: tagList.filter(t => {
        const tagIds = form.getFieldValue('tags') || movie?.tags || [];
        return tagIds.includes(t.id);
      }),
      setSelected: (selected: TagInterface[]) => {
        const tagIds = selected.map(t => t.id);
        form.setFieldValue('tags', tagIds);
        setTagList(selected);
        markFieldChanged('tags');
      },
      fetchSearch: (search: string) =>
        tagApiService
          .fetch(new URLSearchParams({ search, page: '1', page_size: '100' }))
          .then(res => res.results),
      getLabel: (tag: TagInterface) => tag.name || tag.i18n_name?.ko || '',
    };

    return [genreTab, tagTab];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [genreList, tagList, movie]);

  // Video Asset columns
  const videoAssetColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      sorter: true,
      render: (id: number) => {
        return (
          <a
            href={`/assets/edit/${id}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={e => {
              e.preventDefault();
              window.open(`/assets/edit/${id}`, '_blank');
            }}
          >
            {id}
          </a>
        );
      },
    },
    {
      title: 'Filename',
      dataIndex: 'file_name',
      key: 'file_name',
    },
    {
      title: 'Duration',
      dataIndex: 'duration',
      key: 'duration',
      sorter: true,
      render: (duration: number | undefined) => {
        if (!duration) return '-';
        return formatDuration(duration);
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const statusMap: Record<string, { color: string; text: string }> = {
          Complete: { color: 'green', text: 'Complete' },
          Processing: { color: 'blue', text: 'Processing' },
          Transcoding: { color: 'blue', text: 'Transcoding' },
          Failed: { color: 'red', text: 'Failed' },
        };
        const statusInfo = statusMap[status] || { color: 'default', text: status };
        return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
      },
    },
    {
      title: 'Ingest Source',
      dataIndex: 'ingest_source',
      key: 'ingest_source',
      render: (ingestSource: string | undefined) => {
        if (!ingestSource) {
          return <span style={{ color: '#999' }}>-</span>;
        }
        const colorMap: Record<string, string> = {
          mediahold: 'blue',
          kcp: 'purple',
          'in-house': 'green',
        };
        return (
          <Tag color={colorMap[ingestSource.toLowerCase()] || 'default'}>
            {ingestSource.toUpperCase()}
          </Tag>
        );
      },
    },
  ];

  // Stream manifest status helpers
  const getManifestStatusDisplay = (status: string) => {
    const statusValue = status.toLowerCase();
    switch (statusValue) {
      case 'completed':
        return { color: 'green', label: 'Completed' };
      case 'failed':
        return { color: 'red', label: 'Failed' };
      case 'in_progress':
      case 'processing':
        return { color: 'orange', label: 'In Progress' };
      case 'transcoding':
        return { color: 'blue', label: 'Transcoding' };
      case 'uploading':
        return { color: 'blue', label: 'Uploading' };
      case 'empty':
        return { color: 'default', label: 'Waiting' };
      default:
        return { color: 'default', label: status || 'Unknown' };
    }
  };

  const getManifestOverallStatus = (assets: VideoAssetSet[]) => {
    const allManifests = assets.flatMap(a => a.stream_manifests || []);
    if (allManifests.length === 0) return { color: 'default', label: 'No Manifests' };
    const allCompleted = allManifests.every(m => m.status.toLowerCase() === 'completed');
    const hasFailed = allManifests.some(m => m.status.toLowerCase() === 'failed');
    if (allCompleted) return { color: 'green', label: 'All Completed' };
    if (hasFailed) return { color: 'red', label: 'Has Failed' };
    return { color: 'orange', label: 'In Progress' };
  };

  const getServiceManifestStatus = (manifests: StreamManifest[]) => {
    if (manifests.length === 0) return { color: 'default', label: 'No Manifests' };
    const allCompleted = manifests.every(m => m.status.toLowerCase() === 'completed');
    const hasFailed = manifests.some(m => m.status.toLowerCase() === 'failed');
    if (allCompleted) return { color: 'green', label: 'Completed' };
    if (hasFailed) return { color: 'red', label: 'Has Failed' };
    return { color: 'orange', label: 'In Progress' };
  };

  // Linked Video Asset columns
  const linkedVideoAssetColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      render: (id: number) => {
        return (
          <a
            href={`/assets/edit/${id}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={e => {
              e.preventDefault();
              window.open(`/assets/edit/${id}`, '_blank');
            }}
          >
            {id}
          </a>
        );
      },
    },
    {
      title: 'Filename',
      dataIndex: 'file_name',
      key: 'file_name',
    },
    {
      title: 'Duration',
      dataIndex: 'duration',
      key: 'duration',
      render: (duration: number | undefined) => {
        if (!duration) return '-';
        return formatDuration(duration);
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const statusMap: Record<string, { color: string; text: string }> = {
          Complete: { color: 'green', text: 'Complete' },
          Processing: { color: 'blue', text: 'Processing' },
          Transcoding: { color: 'blue', text: 'Transcoding' },
          Failed: { color: 'red', text: 'Failed' },
        };
        const statusInfo = statusMap[status] || { color: 'default', text: status };
        return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
      },
    },
  ];

  // Movie/Trailers/Clips columns

  // Open Distribution admin link
  const handleOpenDistributionAdmin = (
    _contentId: number,
    episodeId: number,
    serviceName: string
  ) => {
    const hostMap: Record<string, string> = {
      odc: IS_PRODUCTION ? 'https://admin.odkmedia.net/odc' : 'https://admin.staging2.odkmedia.net/odc',
      odk: IS_PRODUCTION ? 'https://admin.odkmedia.net/odk' : 'https://admin.staging2.odkmedia.net/odk',
      amasian: IS_PRODUCTION ? 'https://amasian-admin.odkmedia.net' : 'https://amasian-admin.staging2.odkmedia.net',
    };
    if (serviceName === 'amasian') {
      const url = `${hostMap[serviceName]}/video/detail/${episodeId}`;
      window.open(url, '_blank');
    } else if (serviceName === 'odc') {
      const url = `${hostMap[serviceName]}/content/episode/${episodeId}`;
      window.open(url, '_blank');
    } else if (serviceName === 'odk') {
      const url = `${hostMap[serviceName]}/content/episode/${episodeId}`;
      window.open(url, '_blank');
    } else {
      const url = `${hostMap[serviceName]}/content/episode/${episodeId}`;
      window.open(url, '_blank');
    }
  };

  return (
    <Component.BasePageComponent>
      <div style={{ padding: '24px', backgroundColor: movie?.status === 'deleted' ? '#fff1f0' : undefined }}>
        {movie?.status === 'deleted' && (
          <Alert
            message="This content has been deleted."
            type="error"
            showIcon
            banner
            style={{ marginBottom: '16px' }}
          />
        )}
        <Form
          form={form}
          layout="vertical"
          className="movie-edit-single-form"
          onValuesChange={handleValuesChange}
          disabled={movie?.status === 'deleted'}
        >
          {/* Admin Links */}
          <div className="admin-links-container">
            <div
              style={{
                display: 'inline-block',
                padding: '8px 16px',
                backgroundColor: '#fffbe6',
                borderRadius: '4px',
              }}
            >
              <Space>
                {movie?.distribution_sets && movie.distribution_sets.length > 0 ? (
                  movie.distribution_sets.map((dist, index) => (
                    <Button
                      key={index}
                      size="small"
                      onClick={() =>
                        handleOpenDistributionAdmin(
                          dist.content_id,
                          dist.episode_id,
                          dist.service_name
                        )
                      }
                    >
                      {dist.service_name}
                    </Button>
                  ))
                ) : (
                  <div style={{ color: '#999' }}>No Distribution</div>
                )}
              </Space>
            </div>
          </div>

          {/* Movie Metadata Section */}
          <Card title="Movie Metadata" style={{ marginBottom: '24px' }}>
            <I18nFormFields
              fieldName="title"
              required={true}
              fieldType="input"
              value={i18nTitle}
              onChange={handleTitleChange}
            />

            <Form.Item
              label="Slug"
              name="slug"
              style={{ marginTop: '16px', fontWeight: 'bold' }}
              rules={[{ required: true, message: 'Please enter a slug' }]}
            >
              <Input />
            </Form.Item>

            {/* Age Rating and Country of Origin in one row */}
            <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
              <Form.Item
                label="Age Rating (Default G)"
                name="age_rating"
                style={{ flex: 1, marginBottom: 0 }}
              >
                <Select
                  showSearch
                  placeholder="Select age rating"
                  options={MOVIE_AGE_RATING_OPTIONS}
                />
              </Form.Item>

              <Form.Item
                label="Country of Origin"
                name="country_of_origin"
                style={{ flex: 1, marginBottom: 0 }}
              >
                <Select
                  showSearch
                  allowClear
                  placeholder="Select country"
                  onChange={handleCountryOfOriginChange}
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  options={countries.map(country => ({
                    label: country.name,
                    value: country.id,
                  }))}
                />
              </Form.Item>
            </div>

            {/* Movie Ratings and Running Time in one row */}
            <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
              <Form.Item
                label="Movie Ratings (Scale of 10)"
                name="score"
                style={{ flex: 1, marginBottom: 0 }}
              >
                <CustomInputNumber
                  style={{ width: '100%' }}
                  min={0}
                  max={10}
                  precision={1}
                  value={movieRatings}
                  onChange={value => {
                    if (typeof value === 'number') {
                      setMovieRatings(value);
                    } else {
                      setMovieRatings(undefined);
                    }
                  }}
                />
              </Form.Item>

              <Form.Item label="Running Time" name="duration" style={{ flex: 1, marginBottom: 0 }}>
                <TimeInput style={{ width: '100%' }} placeholder="00:00:00" />
              </Form.Item>
            </div>

            {/* cp from odk, cp from odc */}
            <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
              <Form.Item
                label="CP from ODK"
                name="cp_from_odk"
                style={{ flex: 1, marginBottom: 0 }}
              >
                <Select
                  showSearch
                  allowClear
                  placeholder="Search CP from ODK..."
                  filterOption={(input, option) =>
                    (option?.label?.toString() ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  options={cpFromOdkOptions}
                />
              </Form.Item>

              <Form.Item
                label="CP from ODC"
                name="cp_from_odc"
                style={{ flex: 1, marginBottom: 0 }}
              >
                <Select
                  showSearch
                  allowClear
                  placeholder="Search CP from ODC..."
                  filterOption={false}
                  onSearch={handleCpFromOdcSearch}
                  onClear={handleCpFromOdcClear}
                  loading={cpFromOdcLoading}
                  notFoundContent={cpFromOdcLoading ? 'Searching...' : 'No CP found'}
                  onDropdownVisibleChange={handleCpFromOdcDropdownVisibleChange}
                  options={cpFromOdcOptions}
                />
              </Form.Item>

              <Form.Item
                label="CP for third option"
                name="cp_from_odx"
                style={{ flex: 1, marginBottom: 0 }}
              >
                <Input />
              </Form.Item>
            </div>

            <Form.Item label="Synopsis">
              <Tabs
                type="card"
                items={writeLanguages.map(lang => ({
                  key: lang.rfc_code.toLowerCase(),
                  label: `Synopsis ${lang.rfc_code}`,
                  children: (
                    <Input.TextArea
                      rows={6}
                      value={(i18nSynopsis as any)[lang.rfc_code.toLowerCase()] || ''}
                      onChange={e => {
                        setI18nSynopsis({
                          ...i18nSynopsis,
                          [lang.rfc_code.toLowerCase()]: e.target.value,
                        } as I18nFieldData);
                      }}
                      placeholder={lang.rfc_code.toLowerCase() === 'en' ? 'En required *' : ''}
                    />
                  ),
                }))}
              />
            </Form.Item>

            {/* Movie released date and Production Year in one row */}
            <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
              <Form.Item
                label="Movie released date"
                name="release_date_start"
                style={{ flex: 1, marginBottom: 0 }}
                getValueFromEvent={(_date, dateString) => dateString || null}
                getValueProps={value => {
                  if (!value) return { value: null };
                  // Use as-is if value is dayjs object (already converted to PST)
                  if (dayjs.isDayjs(value)) {
                    return { value };
                  }
                  // Parse as date if string (YYYY-MM-DD format)
                  if (typeof value === 'string') {
                    const parsed = dayjs(value, 'YYYY-MM-DD');
                    if (parsed.isValid()) {
                      return { value: parsed };
                    }
                  }
                  // Return null on parse failure
                  return { value: null };
                }}
              >
                <DatePicker
                  format="YYYY-MM-DD"
                  style={{ width: '100%' }}
                  onChange={date => {
                    if (date) {
                      const year = date.year();
                      form.setFieldValue('production_year', year);
                    }
                  }}
                />
              </Form.Item>

              <Form.Item
                label="Production Year"
                name="production_year"
                style={{ flex: 1, marginBottom: 0 }}
              >
                <CustomInputNumber style={{ width: '100%' }} min={1900} max={2100} />
              </Form.Item>
            </div>

            {/* On Air Date */}
            <div style={{ marginBottom: '16px' }}>
              <Form.Item label="On-Air-Date" name="on_air_date">
                <DatePicker format="YYYY-MM-DD" style={{ width: '100%' }} />
              </Form.Item>
            </div>
          </Card>

          {/* Series Posters Section */}
          <Card title="Moive Posters" style={{ marginBottom: '24px' }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, minmax(320px, 1fr))',
                gap: '16px',
                width: '100%',
              }}
            >
              {POSTER_LANGUAGE_OPTIONS.map(lang => {
                const langThumbnails = groupedByLanguage[lang.value] || [];
                const landscapeThumb = langThumbnails.find(t => t.orientation === 'landscape');
                const portraitThumb = langThumbnails.find(t => t.orientation === 'portrait');
                const squareThumb = langThumbnails.find(t => t.orientation === 'square');
                const langCleanThumb = langThumbnails.find(t => t.orientation === 'lang_clean');

                return (
                  <div
                    key={lang.value}
                    style={{
                      border: '1px solid #d9d9d9',
                      padding: '10px',
                      width: '100%',
                      boxSizing: 'border-box',
                      minWidth: 0,
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <div style={{ fontWeight: 'bold', marginBottom: '16px' }}>{lang.label}</div>

                    {/* Landscape */}
                    <div style={{ marginBottom: '16px' }}>
                      <div style={{ fontSize: '12px', color: '#999', marginBottom: '4px' }}>
                        Poster Landscape (1600 x900)
                      </div>
                      {landscapeThumb ? (
                        <div
                          style={{
                            width: '100%',
                            aspectRatio: '16/9',
                            border: '1px dashed #d9d9d9',
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#f5f5f5',
                            position: 'relative',
                            cursor: 'pointer',
                          }}
                          onClick={() => {
                            if (landscapeThumb?.file) {
                              window.open(landscapeThumb.file, '_blank');
                            }
                          }}
                        >
                          <img
                            src={landscapeThumb.file || ''}
                            alt="landscape"
                            style={{
                              maxWidth: '100%',
                              maxHeight: '100%',
                              objectFit: 'contain',
                            }}
                          />
                          <Button
                            type="text"
                            danger
                            icon={<span>×</span>}
                            style={{
                              position: 'absolute',
                              top: '4px',
                              right: '4px',
                              width: '24px',
                              height: '24px',
                              minWidth: '24px',
                              padding: 0,
                              borderRadius: '50%',
                              backgroundColor: 'rgba(255, 255, 255, 0.8)',
                            }}
                            onClick={e => {
                              e.stopPropagation();
                              handleThumbnailRemove(landscapeThumb);
                            }}
                          />
                        </div>
                      ) : (
                        <Upload
                          accept="image/*,.psd"
                          showUploadList={false}
                          beforeUpload={() => false}
                          onChange={handleUploadWithLanguageAndOrientation(lang.value, 'landscape')}
                          style={{ width: '100%', display: 'block' }}
                        >
                          <div
                            style={{
                              width: '100%',
                              aspectRatio: '16/9',
                              border: '1px dashed #d9d9d9',
                              borderRadius: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: '#f5f5f5',
                              cursor: 'pointer',
                              margin: 0,
                              padding: 0,
                            }}
                          >
                            <span style={{ color: '#999', fontSize: '12px' }}>Click to upload</span>
                          </div>
                        </Upload>
                      )}
                    </div>

                    {/* Portrait */}
                    <div style={{ marginBottom: '16px' }}>
                      <div style={{ fontSize: '12px', color: '#999', marginBottom: '4px' }}>
                        Poster Portrait (880 x 1280)
                      </div>
                      {portraitThumb ? (
                        <div
                          style={{
                            width: '100%',
                            aspectRatio: '880/1280',
                            border: '1px dashed #d9d9d9',
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#f5f5f5',
                            position: 'relative',
                            cursor: 'pointer',
                          }}
                          onClick={() => {
                            if (portraitThumb?.file) {
                              window.open(portraitThumb.file, '_blank');
                            }
                          }}
                        >
                          <img
                            src={portraitThumb.file || ''}
                            alt="portrait"
                            style={{
                              maxWidth: '100%',
                              maxHeight: '100%',
                              objectFit: 'contain',
                            }}
                          />
                          <Button
                            type="text"
                            danger
                            icon={<span>×</span>}
                            style={{
                              position: 'absolute',
                              top: '4px',
                              right: '4px',
                              width: '24px',
                              height: '24px',
                              minWidth: '24px',
                              padding: 0,
                              borderRadius: '50%',
                              backgroundColor: 'rgba(255, 255, 255, 0.8)',
                            }}
                            onClick={e => {
                              e.stopPropagation();
                              handleThumbnailRemove(portraitThumb);
                            }}
                          />
                        </div>
                      ) : (
                        <Upload
                          accept="image/*,.psd"
                          showUploadList={false}
                          beforeUpload={() => false}
                          onChange={handleUploadWithLanguageAndOrientation(lang.value, 'portrait')}
                          style={{ width: '100%', display: 'block' }}
                        >
                          <div
                            style={{
                              width: '100%',
                              aspectRatio: '880/1280',
                              border: '1px dashed #d9d9d9',
                              borderRadius: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: '#f5f5f5',
                              cursor: 'pointer',
                              margin: 0,
                              padding: 0,
                            }}
                          >
                            <span style={{ color: '#999', fontSize: '12px' }}>Click to upload</span>
                          </div>
                        </Upload>
                      )}
                    </div>
                    <div style={{ marginBottom: '16px' }}>
                      <div style={{ fontSize: '12px', color: '#999', marginBottom: '4px' }}>
                        Square (1080 x 1080)
                      </div>
                      {squareThumb ? (
                        <div
                          style={{
                            width: '100%',
                            aspectRatio: '1/1',
                            border: '1px dashed #d9d9d9',
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#f5f5f5',
                            position: 'relative',
                            cursor: 'pointer',
                          }}
                          onClick={() => {
                            if (squareThumb?.file) {
                              window.open(squareThumb.file, '_blank');
                            }
                          }}
                        >
                          <img
                            src={squareThumb.file || ''}
                            alt="square"
                            style={{
                              maxWidth: '100%',
                              maxHeight: '100%',
                              objectFit: 'contain',
                            }}
                          />
                          <Button
                            type="text"
                            danger
                            icon={<span>×</span>}
                            style={{
                              position: 'absolute',
                              top: '4px',
                              right: '4px',
                              width: '24px',
                              height: '24px',
                              minWidth: '24px',
                              padding: 0,
                              borderRadius: '50%',
                              backgroundColor: 'rgba(255, 255, 255, 0.8)',
                            }}
                            onClick={e => {
                              e.stopPropagation();
                              handleThumbnailRemove(squareThumb);
                            }}
                          />
                        </div>
                      ) : (
                        <Upload
                          accept="image/*,.psd"
                          showUploadList={false}
                          beforeUpload={() => false}
                          onChange={handleUploadWithLanguageAndOrientation(lang.value, 'square')}
                          style={{ width: '100%', display: 'block' }}
                        >
                          <div
                            style={{
                              width: '100%',
                              aspectRatio: '1/1',
                              border: '1px dashed #d9d9d9',
                              borderRadius: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: '#f5f5f5',
                              cursor: 'pointer',
                              margin: 0,
                              padding: 0,
                            }}
                          >
                            <span style={{ color: '#999', fontSize: '12px' }}>Click to upload</span>
                          </div>
                        </Upload>
                      )}
                    </div>

                    {/* Lang Clean */}
                    <div style={{ marginBottom: '16px' }}>
                      <div style={{ fontSize: '12px', color: '#999', marginBottom: '4px' }}>
                        Clean (1600 x 900)
                      </div>
                      {langCleanThumb ? (
                        <div
                          style={{
                            width: '100%',
                            aspectRatio: '16/9',
                            border: '1px dashed #d9d9d9',
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#f5f5f5',
                            position: 'relative',
                            cursor: 'pointer',
                          }}
                          onClick={() => {
                            if (langCleanThumb?.file) {
                              window.open(langCleanThumb.file, '_blank');
                            }
                          }}
                        >
                          <img
                            src={langCleanThumb.file || ''}
                            alt="lang clean"
                            style={{
                              maxWidth: '100%',
                              maxHeight: '100%',
                              objectFit: 'contain',
                            }}
                          />
                          <Button
                            type="text"
                            danger
                            icon={<span>×</span>}
                            style={{
                              position: 'absolute',
                              top: '4px',
                              right: '4px',
                              width: '24px',
                              height: '24px',
                              minWidth: '24px',
                              padding: 0,
                              borderRadius: '50%',
                              backgroundColor: 'rgba(255, 255, 255, 0.8)',
                            }}
                            onClick={e => {
                              e.stopPropagation();
                              handleThumbnailRemove(langCleanThumb);
                            }}
                          />
                        </div>
                      ) : (
                        <Upload
                          accept="image/*,.psd"
                          showUploadList={false}
                          beforeUpload={() => false}
                          onChange={handleUploadWithLanguageAndOrientation(
                            lang.value,
                            'lang_clean'
                          )}
                          style={{ width: '100%', display: 'block' }}
                        >
                          <div
                            style={{
                              width: '100%',
                              aspectRatio: '16/9',
                              border: '1px dashed #d9d9d9',
                              borderRadius: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: '#f5f5f5',
                              cursor: 'pointer',
                              margin: 0,
                              padding: 0,
                            }}
                          >
                            <span style={{ color: '#999', fontSize: '12px' }}>Click to upload</span>
                          </div>
                        </Upload>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {id && id !== 'new' && (
              <div style={{ marginTop: '16px', textAlign: 'center' }}>
                <Button type="primary" onClick={() => handleThumbnailUpload()}>
                  Save Posters
                </Button>
              </div>
            )}
          </Card>
          <Card title="Clip & Trailer" style={{ marginBottom: '24px' }}>
            <ClipTrailerComponent />
          </Card>
          {/* Video Assets Section */}
          <Card title="Video Assets" style={{ marginBottom: '24px' }}>
            <Space style={{ marginBottom: '16px' }}>
              <Input
                placeholder="keyword"
                value={videoAssetKeyword}
                onChange={e => setVideoAssetKeyword(e.target.value)}
                onPressEnter={handleVideoAssetSearch}
                style={{ width: '200px' }}
              />
              <Button onClick={handleVideoAssetSearch} loading={isLoadingVideoAssets}>
                Search
              </Button>
            </Space>

            <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
              {/* Video Asset Search Table */}
              <div style={{ flex: 1 }}>
                <Spin spinning={isLoadingVideoAssets}>
                  <Table
                    columns={videoAssetColumns}
                    dataSource={videoAssets}
                    rowKey="id"
                    rowSelection={{
                      selectedRowKeys: selectedVideoAssets,
                      onChange: (selectedKeys: React.Key[]) => {
                        setSelectedVideoAssets(selectedKeys as number[]);
                      },
                      type: 'radio',
                    }}
                    pagination={{
                      current: videoAssetCurrentPage,
                      total: videoAssetTotal,
                      pageSize: 10,
                      showSizeChanger: false,
                      showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                    }}
                    onChange={pagination => {
                      if (pagination.current) {
                        handleVideoAssetPageChange(pagination.current);
                      }
                    }}
                    size="small"
                  />
                </Spin>
              </div>

              {/* Link/Unlink Buttons */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  gap: '8px',
                  padding: '0 8px',
                }}
              >
                <Button
                  type="primary"
                  onClick={handleLinkVideoAssets}
                  disabled={selectedVideoAssets.length === 0}
                >
                  Link &gt;
                </Button>
                <Button onClick={handleUnlinkVideoAssets} disabled={!linkedVideoAsset}>
                  &lt; Unlink
                </Button>
              </div>

              {/* Linked Video Asset Table */}
              <div style={{ flex: 1 }}>
                {linkedVideoAsset ? (
                  <>
                    <Table
                      columns={linkedVideoAssetColumns}
                      dataSource={[linkedVideoAsset]}
                      rowKey="id"
                      pagination={false}
                      size="small"
                    />
                    {hasPlayableVideoUrl(linkedVideoAsset) ? (
                      <div style={{ marginTop: '16px' }}>
                        <div
                          style={{
                            padding: '8px 16px',
                            backgroundColor: '#fffbe6',
                            borderRadius: '4px',
                            marginBottom: '8px',
                          }}
                        >
                          Preview Video
                        </div>
                        <VideoPlayer
                          options={{
                            sources: [
                              {
                                src: getPlayableVideoUrl(linkedVideoAsset) || '',
                                type: 'video/mp4',
                              },
                            ],
                            controls: true,
                          }}
                        />
                      </div>
                    ) : (
                      <div style={{ marginTop: '16px' }}>
                        <div
                          style={{
                            padding: '8px 16px',
                            backgroundColor: '#fffbe6',
                            borderRadius: '4px',
                            marginBottom: '8px',
                          }}
                        >
                          No Preview Video
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                    No linked video asset
                  </div>
                )}
              </div>
            </div>
          </Card>

          {/* Content Stream Manifests Section */}
          {(() => {
            const assetsWithManifests = existingContentVideoAssets.filter(
              a => a.stream_manifests && a.stream_manifests.length > 0
            );
            if (assetsWithManifests.length === 0) return null;

            const overallStatus = getManifestOverallStatus(assetsWithManifests);

            return (
              <Card style={{ marginBottom: '24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                  <GlobalOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                  <Text strong>Content Stream Manifests</Text>
                  <Tag color={overallStatus.color} style={{ marginLeft: '12px' }}>
                    {overallStatus.label}
                  </Tag>
                </div>
                <Collapse
                  size="small"
                  defaultActiveKey={[]}
                  items={assetsWithManifests.map(asset => {
                    const manifests = asset.stream_manifests || [];
                    const serviceStatus = getServiceManifestStatus(manifests);
                    return {
                      key: asset.service_name || `asset-${asset.id}`,
                      label: (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                          }}
                        >
                          <Text strong>{asset.service_name || 'Default'}</Text>
                          <Tag color="blue" style={{ fontSize: '11px' }}>
                            {manifests.length} manifests
                          </Tag>
                          <Tag color={serviceStatus.color} style={{ fontSize: '11px' }}>
                            {serviceStatus.label}
                          </Tag>
                          <Button
                            size="small"
                            type="primary"
                            loading={
                              !!regeneratingManifests[`${asset.id}-${asset.service_name}`]
                            }
                            onClick={async e => {
                              e.stopPropagation();
                              if (!asset.id) {
                                message.error('Asset ID not found.');
                                return;
                              }
                              const key = `${asset.id}-${asset.service_name}`;
                              try {
                                setRegeneratingManifests(prev => ({
                                  ...prev,
                                  [key]: true,
                                }));
                                await contentAssetService.addSubtitle(asset.id);
                                message.success(
                                  `Stream manifests regeneration request sent for ${asset.service_name || 'Default'}.`
                                );
                              } catch (error) {
                                console.error(
                                  'Failed to regenerate stream manifests:',
                                  error
                                );
                                message.error('Failed to regenerate stream manifests.');
                              } finally {
                                setRegeneratingManifests(prev => ({
                                  ...prev,
                                  [key]: false,
                                }));
                              }
                            }}
                          >
                            Regenerate Stream Manifests
                          </Button>
                        </div>
                      ),
                      children: (
                        <Row gutter={[8, 8]}>
                          {manifests.map((manifest, idx) => {
                            const statusDisplay = getManifestStatusDisplay(manifest.status);
                            const hlsUrl = manifest.stream_url_full || manifest.stream_url;
                            const isCompleted = manifest.status.toLowerCase() === 'completed';
                            const isFailed = manifest.status.toLowerCase() === 'failed';
                            const isInProgress = [
                              'in_progress', 'processing', 'creating', 'start',
                              'transcoding', 'uploading',
                            ].includes(manifest.status.toLowerCase());
                            return (
                              <Col xs={24} sm={12} lg={8} key={idx}>
                                <Card
                                  size="small"
                                  style={{
                                    borderRadius: '6px',
                                    border: `1px solid ${
                                      statusDisplay.color === 'green'
                                        ? '#b7eb8f'
                                        : statusDisplay.color === 'red'
                                          ? '#ffa39e'
                                          : statusDisplay.color === 'orange'
                                            ? '#ffe58f'
                                            : '#d9d9d9'
                                    }`,
                                  }}
                                >
                                  <div
                                    style={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                    }}
                                  >
                                    <Text strong style={{ fontSize: '12px' }}>
                                      {manifest.type?.toUpperCase()} - {manifest.preset}
                                    </Text>
                                    <Tag
                                      color={statusDisplay.color}
                                      style={{ fontSize: '10px' }}
                                    >
                                      {statusDisplay.label}
                                    </Tag>
                                  </div>
                                  {isCompleted && hlsUrl && (
                                    <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                                      <ManifestSubtitleTags url={hlsUrl} />
                                    </div>
                                  )}
                                  {isCompleted && hlsUrl && (
                                    <div style={{ textAlign: 'center' }}>
                                      <Text
                                        style={{
                                          fontSize: '9px',
                                          color: '#999',
                                          wordBreak: 'break-all',
                                          display: 'block',
                                          marginBottom: '4px',
                                        }}
                                      >
                                        {hlsUrl}
                                      </Text>
                                      <Button
                                        size="small"
                                        type="link"
                                        style={{ fontSize: '11px', padding: 0 }}
                                        onClick={() => {
                                          navigator.clipboard.writeText(hlsUrl);
                                          message.success('URL copied');
                                        }}
                                      >
                                        Copy URL
                                      </Button>
                                    </div>
                                  )}
                                  {isCompleted && hlsUrl && (
                                    <div style={{ marginTop: '12px' }}>
                                      <Preview videoUrl={hlsUrl} />
                                    </div>
                                  )}
                                  {isInProgress && (
                                    <div style={{ textAlign: 'center', padding: '8px 0' }}>
                                      <Spin size="small" />
                                    </div>
                                  )}
                                  {isFailed && (
                                    <div style={{ textAlign: 'center', padding: '8px 0' }}>
                                      <Text type="danger" style={{ fontSize: '11px' }}>
                                        Processing Failed
                                      </Text>
                                    </div>
                                  )}
                                </Card>
                              </Col>
                            );
                          })}
                        </Row>
                      ),
                    };
                  })}
                />
              </Card>
            );
          })()}

          {/* Enhanced Cast Section */}
          <Card title="Enhanced Cast" style={{ marginBottom: '24px' }}>
            <EnhancedCastEditor
              value={enhancedCast}
              onChange={(value: any) => {
                setEnhancedCast(
                  value.map((item: any) => ({
                    cast: item.cast,
                    type: item.type as 'director' | 'writer' | 'actor',
                  }))
                );
              }}
              peopleList={searchedPeopleList}
              onSearchPeople={searchPeopleForEnhancedCast}
            />
          </Card>

          {/* Genres & Tags Section */}
          <Card title="Genres & Tags" style={{ marginBottom: '24px' }}>
            <Form.Item>
              <Form.Item name="genres" hidden />
              <Form.Item name="tags" hidden />
              <CustomSelector tabs={tabs} />
            </Form.Item>
          </Card>

          {/* 11DB Metadata Section */}
          <Card
            title={`11DB Metadata ${elevenDbContentId ? `{${elevenDbContentId}}` : ''}`}
            style={{ marginBottom: '24px' }}
          >
            <Form.Item label="11DB Content ID">
              <Input
                value={elevenDbContentId}
                onChange={e => setElevenDbContentId(e.target.value)}
                placeholder="Enter 11DB content ID"
              />
            </Form.Item>

            <Form.Item label="Awards">
              <Input
                placeholder="Enter award name"
                value={elevenDbAwards}
                onChange={e => setElevenDbAwards(e.target.value)}
              />
            </Form.Item>

            <Form.Item label="Awards Category">
              <Input
                style={{ width: '100%' }}
                placeholder="Enter award category"
                value={elevenDbAwardsCategory}
                onChange={e => setElevenDbAwardsCategory(e.target.value)}
              />
            </Form.Item>

            <Form.Item label="Related Series">
              <Input
                style={{ width: '100%' }}
                placeholder="Enter series ID or name"
                value={elevenDbRelatedSeries}
                onChange={e => {
                  setElevenDbRelatedSeries(e.target.value);
                }}
              />
            </Form.Item>

            <Form.Item label="Related Titles">
              <Input
                style={{ width: '100%' }}
                placeholder="Enter title ID or name"
                value={elevenDbRelatedTitles}
                onChange={e => {
                  setElevenDbRelatedTitles(e.target.value);
                }}
              />
            </Form.Item>

            <Form.Item label="Linked Articles">
              <Input
                style={{ width: '100%' }}
                placeholder="Enter article URL"
                value={elevenDbLinkedArticles}
                onChange={e => {
                  setElevenDbLinkedArticles(e.target.value);
                }}
              />
            </Form.Item>

            <div style={{ display: 'flex', gap: '16px' }}>
              <Form.Item label="User Ratings">
                <Input
                  placeholder="Enter user ratings"
                  value={elevenDbUserRatings}
                  onChange={e => setElevenDbUserRatings(e.target.value)}
                />
              </Form.Item>

              <Form.Item label="Critics Ratings">
                <Input
                  placeholder="Enter critics ratings"
                  value={elevenDbCriticsRatings}
                  onChange={e => setElevenDbCriticsRatings(e.target.value)}
                />
              </Form.Item>

              <Form.Item label="Movie Box Office">
                <Input
                  placeholder="Enter movie box office"
                  value={elevenDbMovieBoxOffice}
                  onChange={e => setElevenDbMovieBoxOffice(e.target.value)}
                />
              </Form.Item>
            </div>
          </Card>

          {/* Service Auto Distribution Section */}
          <Card
            title="Service Auto Distribution"
            style={{ marginBottom: '24px' }}
            extra={
              id && id !== 'new' && (
                <Button
                  icon={<SyncOutlined spin={resyncLoading} />}
                  loading={resyncLoading}
                  onClick={handleResyncDistribution}
                >
                  Resync Distribution
                </Button>
              )
            }
          >
            <p style={{ marginBottom: '16px', color: '#666' }}>
              Movie&apos;s Service Auto Distribution settings.
            </p>

            <Table
              dataSource={(() => {
                const data: any[] = [];
                SERVICE_OPTIONS.forEach(service => {
                  const dist = distributions.find(
                    d =>
                      d.service_name === service.name &&
                      d.environment === (IS_PRODUCTION ? 'production' : 'staging')
                  );
                  data.push({
                    key: `${service.name}-production`,
                    service: service.name,
                    environment: IS_PRODUCTION ? 'production' : 'staging',
                    dist: dist || {
                      service_name: service.name,
                      environment: IS_PRODUCTION ? 'production' : 'staging',
                      enabled: false,
                      auto_publish: false,
                      subtitle_langs: [],
                    },
                  });
                });

                return data;
              })()}
              columns={[
                {
                  title: 'Service',
                  dataIndex: 'service',
                  key: 'service',
                  width: '320px',
                  render: (text: string, record: any) => {
                    if (record.isDivider) {
                      return {
                        children: <div style={{ height: '1px', background: '#e8e8e8' }} />,
                        props: { colSpan: 3 },
                      };
                    }
                    return (
                      <Checkbox
                        checked={record.dist.enabled || false}
                        onChange={e => {
                          // Toggle enabled only
                          updateDistribution(record.service, record.environment, {
                            enabled: e.target.checked,
                          });
                        }}
                      >
                        <span style={{ fontWeight: 500 }}>{text}</span>
                      </Checkbox>
                    );
                  },
                },
                {
                  title: 'Auto Publish',
                  key: 'auto_publish',
                  width: '150px',
                  render: (_: any, record: any) => {
                    if (record.isDivider) {
                      return { props: { colSpan: 0 } };
                    }
                    return (
                      <Checkbox
                        checked={record.dist.auto_publish || false}
                        disabled={!record.dist.enabled}
                        onChange={e =>
                          updateDistribution(record.service, record.environment, {
                            auto_publish: e.target.checked,
                          })
                        }
                      >
                        Auto Publish
                      </Checkbox>
                    );
                  },
                },
                {
                  title: 'Subtitles',
                  key: 'subtitles',
                  render: (_: any, record: any) => {
                    if (record.isDivider) {
                      return { props: { colSpan: 0 } };
                    }
                    return (
                      <Space wrap>
                        {subtitleLanguages.map(lang => (
                          <Checkbox
                            key={lang.rfc_code.toLowerCase()}
                            checked={record.dist.subtitle_langs?.includes(lang.rfc_code.toLowerCase()) || false}
                            disabled={!record.dist.enabled}
                            onChange={e => {
                              const newSubtitles = e.target.checked
                                ? [...(record.dist.subtitle_langs || []), lang.rfc_code.toLowerCase()]
                                : (record.dist.subtitle_langs || []).filter(
                                    (s: string) => s !== lang.rfc_code.toLowerCase()
                                  );
                              updateDistribution(record.service, record.environment, {
                                subtitle_langs: newSubtitles,
                              });
                            }}
                          >
                            {lang.rfc_code}
                          </Checkbox>
                        ))}
                      </Space>
                    );
                  },
                },
              ]}
              pagination={false}
              bordered
            />
          </Card>

          {/* Save Button */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'right',
              alignItems: 'center',
              gap: '16px',
              marginTop: '32px',
            }}
          >
            {/* <Card>
              <Form.Item label="Movie Status" name="status" style={{ marginBottom: 0 }}>
                <Select
                  style={{ width: '200px' }}
                  onChange={value => {
                    form.setFieldValue('status', value);
                  }}
                >
                  <Select.Option value="published">Enabled</Select.Option>
                  <Select.Option value="unpublished">Disabled</Select.Option>
                </Select>
              </Form.Item>
            </Card> */}
            {!videoAssetsLoaded && (
              <Tag color="processing">Loading data...</Tag>
            )}
            {movie?.status !== 'deleted' && (
              <Button type="primary" size="large" onClick={handleSave}>
                Save Movie
              </Button>
            )}
          </div>
        </Form>
      </div>
    </Component.BasePageComponent>
  );
};

export default MovieEditSingle;
